import React, { useEffect, useState } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Dialog, DialogActions, DialogContent, Typography,
  TablePagination, InputAdornment, TextField, Box, Paper,
  IconButton, Divider, Menu, MenuItem
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from '@mui/icons-material/Edit';
import api from "../api";
import AgregarCombustible from "./utilities/AgregarCombustible"; // <-- Component for adding fuel entries
import EditarCombustible from "./utilities/EditarCombustible"; // <-- Component for editing fuel entries
import DetalleCombustible from "./utilities/DetalleCombustible"; // <-- Component for viewing detailed fuel entry
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import * as XLSX from "xlsx";
import DownloadIcon from '@mui/icons-material/Download';

const Combustibles = () => {
  const [combustibles, setCombustibles] = useState([]);
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCombustible, setSelectedCombustible] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuCombustible, setMenuCombustible] = useState(null);

  useEffect(() => {
    fetchCombustibles();
  }, []);

  const fetchCombustibles = () => {
    api.get("combustibles/")
      .then((response) => {
        console.log("API Response:", response.data);  // Log response to check data structure
        setCombustibles(response.data);
      })
      .catch((error) => {
        console.error("Error al obtener combustibles:", error);
      });
  };

  const handleDelete = (id) => {
    if (window.confirm("¿Estás seguro de que quieres eliminar este combustible?")) {
      api.delete(`combustibles/${id}/`)
        .then(() => {
          setCombustibles(combustibles.filter(c => c.id !== id));
        })
        .catch(error => {
          console.error("Error al eliminar el combustible:", error);
        });
    }
  };

  const handleMenuOpen = (event, combustible) => {
    event.stopPropagation();  // Prevent the row click event from firing
    setAnchorEl(event.currentTarget);
    setMenuCombustible(combustible);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuCombustible(null);
  };

  const handleEditOpen = () => {
    setOpenEdit(true);
    setAnchorEl(null);
  };

  const handleRowClick = (combustible) => {
    setSelectedCombustible(combustible); // Update with the selected combustible
  };

  const handleDetailClose = () => {
    setSelectedCombustible(null); // Reset the selected combustible when closing
  };

  const handleCombustibleUpdated = (updatedCombustible) => {
    setCombustibles((prevCombustibles) => {
      const exists = prevCombustibles.some((i) => i.id === updatedCombustible.id);
      if (exists) {
        return prevCombustibles.map((i) =>
          i.id === updatedCombustible.id ? updatedCombustible : i
        );
      } else {
        return [...prevCombustibles, updatedCombustible];
      }
    });

    setOpen(false);      // para AgregarInfraccion
    setOpenEdit(false);  // para EditarInfraccion
  };

  const normalize = (str) =>
    str?.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  const filteredCombustibles = combustibles.filter((item) => {
    const search = normalize(searchTerm);
    return (
      normalize(item.vehiculo_nombre || "").includes(search) ||
      normalize(
        new Date(item.fecha_y_hora).toLocaleString("es-ES", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      ).includes(search) ||
      normalize(String(item.litros_unidades || "")).includes(search) ||
      normalize(String(item.imp_tot_yer || "")).includes(search)
    );
  });

  // Download all records as XLS
  const handleDownloadXLS = async () => {
    try {
      const response = await api.get("combustibles/");
      const allData = response.data;

      const worksheet = XLSX.utils.json_to_sheet(allData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Combustibles");

      XLSX.writeFile(workbook, "consumo.xlsx");
    } catch (error) {
      alert("Error al descargar los datos.");
      console.error(error);
    }
  };

  // Import combustibles XLS
  const handleImportCombustibles = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post('combustibles/import/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert('Combustibles importados correctamente');
      fetchCombustibles();
    } catch (err) {
      alert('Error al importar combustibles');
    }
  };

  // Import YPF combustibles XLS
  const handleImportYPF = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post('combustibles/import-ypf/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert('Combustibles YPF importados correctamente');
      fetchCombustibles();
    } catch (err) {
      if (err.response && err.response.status === 409 && err.response.data && err.response.data.error) {
        alert(err.response.data.error);
      } else {
        alert('Error al importar combustibles YPF');
      }
    }
  };

  return (
    <Paper elevation={3} sx={{ padding: 3, borderRadius: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={700} color='#152d51'>
          Consumo
        </Typography>
        <Box display="flex" gap={1}>
          <TextField
            variant="outlined"
            label="Buscar consumo"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ width: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Button onClick={handleDownloadXLS} variant="outlined" color="success">
            Descargar XLS&nbsp;<DownloadIcon sx={{ fontSize: 18, marginTop: "-2px" }} />
          </Button>
          {/* Import combustibles XLS */}
          <input
            type="file"
            accept=".xls,.xlsx"
            style={{ display: 'none' }}
            id="import-combustibles"
            onChange={handleImportCombustibles}
          />
          <label htmlFor="import-combustibles">
            <Button component="span" variant="outlined" color="primary">
              Importar Combustibles XLS
            </Button>
          </label>
          {/* YPF XLS Import */}
          <input
            type="file"
            accept=".xls,.xlsx"
            style={{ display: 'none' }}
            id="import-ypf"
            onChange={handleImportYPF}
          />
          <label htmlFor="import-ypf">
            <Button component="span" style={{ border: "1px solid #51b53f", borderRadius: "6px", color: "#51b53f" }}>
              Importar XLS YPF&nbsp;<CloudUploadIcon sx={{ fontSize: 18, marginTop: "-2px" }} />
            </Button>
          </label>
          <Button onClick={() => setOpen(true)} style={{ border: "1px solid #3f51b5", borderRadius: "6px", color: "#3f51b5" }}>
            Agregar Consumo <AddIcon sx={{ fontSize: 18, marginTop: "-2px" }} />
          </Button>
        </Box>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <TableContainer>
        <Table>
          <TableHead sx={{ backgroundColor: "rgb(244, 246, 248)" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Vehículo</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Fecha y hora</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Cantidad</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Costo</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCombustibles
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((combustible) => (
                <TableRow key={combustible.id} sx={{ '&:hover': { backgroundColor: '#f1f1f1', cursor: 'pointer' } }} onClick={() => handleRowClick(combustible)}>
                  <TableCell>{combustible.vehiculo_nombre}</TableCell>
                  <TableCell>
                    {new Date(combustible.fecha_y_hora).toLocaleString("es-ES", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    })}
                  </TableCell>
                  <TableCell>{combustible.litros_unidades} L</TableCell>
                  <TableCell>{combustible.imp_tot_yer} ARS</TableCell>
                  <TableCell>
                    <IconButton onClick={(event) => handleMenuOpen(event, combustible)}>
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={combustibles.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); }}
      />
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEditOpen}>
          <EditIcon color="primary" sx={{ mr: 1 }} /> Editar
        </MenuItem>
        <MenuItem onClick={() => { handleDelete(menuCombustible.id); handleMenuClose(); }}>
          <DeleteIcon color="error" sx={{ mr: 1 }} /> Eliminar
        </MenuItem>
      </Menu>

      {/* Dialog for Detail */}
      <Dialog open={!!selectedCombustible} onClose={handleDetailClose} fullWidth maxWidth="sm">
        <DialogContent>
          <DetalleCombustible selectedCombustible={selectedCombustible} handleDetailClose={handleDetailClose} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDetailClose} color="secondary">Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogContent>
          <AgregarCombustible handleClose={() => setOpen(false)} onUpdate={handleCombustibleUpdated} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="secondary">Cancelar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth maxWidth="sm">
        <DialogContent>
          <EditarCombustible combustible={menuCombustible} handleClose={() => setOpenEdit(false)} onUpdate={handleCombustibleUpdated} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)} color="secondary">Cancelar</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Combustibles;
